/**
 * What a hand actually IS on a board: the made hand, how much of it comes from the
 * hole cards, and what it is drawing to.
 *
 * The evaluator already ranks seven cards against each other, which is all a showdown
 * needs. A bot needs more than a ranking. "Second pair" and "a flush draw" are worth
 * about the same in raw equity on many flops and call for opposite actions — one
 * wants to see a cheap showdown, the other wants the pot to grow or end now. Nothing
 * in a scalar score distinguishes them.
 *
 * Everything here is derived from the cards, not estimated. The uncertainty lives in
 * equity.ts; this file is arithmetic.
 */
import { evaluate7, rankOf, suitOf } from "../pipeline/solve/evaluator";
import type { Card } from "./types";

export type MadeCategory =
  | "air" | "pair" | "two_pair" | "trips" | "straight" | "flush"
  | "full_house" | "quads" | "straight_flush";

/**
 * Where a pair sits relative to the board, which is what decides how it plays.
 * `over` is a pocket pair above every board card; `top`/`second`/`third` pair the
 * board from the top down; `weak` is anything lower.
 */
export type PairKind = "none" | "over" | "top" | "second" | "third" | "weak" | "board";

export interface HandClass {
  category: MadeCategory;
  pair: PairKind;
  /** 0..12 rank of the kicker that plays, or -1 when none does. */
  kicker: number;
  /** How many hole cards are part of the made hand itself, ignoring kicker slots. */
  holeCardsUsed: 0 | 1 | 2;
  /** Shorthand for holeCardsUsed === 2. */
  usesBoth: boolean;
  /** The category the board makes on its own — what every player at the table has. */
  boardCategory: MadeCategory;
  /** The made hand is entirely on the board — everyone has it. */
  playingBoard: boolean;

  /* ---------------------------------------------------------------- draws --- */
  /** Four to a flush, needing one card. */
  flushDraw: boolean;
  /** Four to a flush where the hero holds the highest missing card of the suit. */
  nutFlushDraw: boolean;
  /** Three to a flush, needing two. Worth something on the flop, nothing on the turn. */
  backdoorFlush: boolean;
  /** Eight outs to a straight. */
  openEnded: boolean;
  /** Four outs to a straight. */
  gutshot: boolean;
  /** Three to a straight needing two running cards. */
  backdoorStraight: boolean;
  /** Hole cards above every board card, with no pair — the cheapest kind of equity. */
  overcards: number;
  /** Rough count of cards that improve the hand to something likely to be best. */
  outs: number;
}

const ALL_RANKS = 13;

function rankMask(cards: readonly Card[]): number {
  let m = 0;
  for (const c of cards) m |= 1 << rankOf(c);
  return m;
}

/** Straight completions available to a rank mask: how many distinct ranks finish one. */
function straightOuts(mask: number): number[] {
  const out: number[] = [];
  for (let r = 0; r < ALL_RANKS; r++) {
    if (mask & (1 << r)) continue;
    if (hasStraight(mask | (1 << r))) out.push(r);
  }
  return out;
}

/**
 * The top rank of the best straight in a rank mask, or -1. The wheel reports 3, the
 * rank of its five, which is what makes A2345 lose to 23456.
 */
export function straightHigh(mask: number): number {
  for (let hi = 12; hi >= 4; hi--) {
    let ok = true;
    for (let k = 0; k < 5; k++) if (!(mask & (1 << (hi - k)))) { ok = false; break; }
    if (ok) return hi;
  }
  if ((mask & (1 << 12)) !== 0 && (mask & 0b1111) === 0b1111) return 3;
  return -1;
}

export function hasStraight(mask: number): boolean {
  for (let hi = 12; hi >= 4; hi--) {
    let ok = true;
    for (let k = 0; k < 5; k++) if (!(mask & (1 << (hi - k)))) { ok = false; break; }
    if (ok) return true;
  }
  // The wheel, where the ace plays low.
  return (mask & (1 << 12)) !== 0 && (mask & 0b1111) === 0b1111;
}

/** Rank counts across the seven cards, and per-suit counts. */
function counts(cards: readonly Card[]): { rc: Int8Array; sc: Int8Array } {
  const rc = new Int8Array(ALL_RANKS);
  const sc = new Int8Array(4);
  for (const c of cards) { rc[rankOf(c)] = rc[rankOf(c)]! + 1; sc[suitOf(c)] = sc[suitOf(c)]! + 1; }
  return { rc, sc };
}

export function classify(hole: readonly Card[], board: readonly Card[]): HandClass {
  if (hole.length !== 2) throw new Error("classify expects exactly two hole cards");
  if (board.length > 5) throw new Error(`board of ${board.length} cards`);

  const all = [...hole, ...board];
  const { rc, sc } = counts(all);
  const boardCounts = counts(board);
  const boardRanks = board.map(rankOf);
  const topBoard = boardRanks.length ? Math.max(...boardRanks) : -1;
  const sortedBoard = [...boardRanks].sort((a, b) => b - a);

  /* ------------------------------------------------------------ made hand --- */
  const trips = highestWithCount(rc, 3);
  const pairs: number[] = [];
  for (let r = ALL_RANKS - 1; r >= 0; r--) {
    if (rc[r]! === 2 || (rc[r]! === 3 && r !== trips)) pairs.push(r);
  }

  const category = categoryOf(all);
  const mask = rankMask(all);
  const straight = category === "straight" || category === "straight_flush";

  let flushSuit = -1;
  for (let s = 0; s < 4; s++) if (sc[s]! >= 5) flushSuit = s;

  /* ------------------------------------------------------- pair position --- */
  const h0 = rankOf(hole[0]!), h1 = rankOf(hole[1]!);
  const pocket = h0 === h1;
  let pair: PairKind = "none";

  if (category === "pair" || category === "two_pair" || category === "trips") {
    const best = pairs.length ? Math.max(...pairs, trips >= 0 ? trips : -1) : trips;
    if (best >= 0) {
      const fromHole = best === h0 || best === h1;
      const onBoard = (boardCounts.rc[best] ?? 0) >= 2;
      if (!fromHole && onBoard) pair = "board";
      else if (pocket && best === h0 && best > topBoard) pair = "over";
      else {
        const idx = sortedBoard.indexOf(best);
        pair = idx === 0 ? "top" : idx === 1 ? "second" : idx === 2 ? "third"
             : idx > 2 ? "weak" : (best > topBoard ? "over" : "weak");
      }
    }
  }

  /**
   * Does the hand use both hole cards?
   *
   * Two tests, because neither alone is right at every street.
   *
   * By CATEGORY: the question is whether the card is part of what makes the hand, not
   * whether it happens to be the fifth card. A9 on A-9-4 has two pair with both cards;
   * A9 on A-4-4 has the same two pair with only the ace.
   *
   * And by RANK, from the turn onwards, where removing a card leaves five and the
   * evaluator can compare like with like. The category test alone missed every
   * upgrade WITHIN a category: 98 on 7-6-5-4-3 makes a nine-high straight and the
   * board makes a seven-high one, and the category is "straight" either way — so the
   * hand was reported as using neither hole card while both of them were playing.
   */
  const fullScore = evaluate7(all);
  const playsAlone = (other: Card): boolean => {
    if (board.length < 4) return false;      // fewer than five cards without it
    return evaluate7([other, ...board]) < fullScore;
  };
  const holeCardsUsed =
    (categoryOf([hole[1]!, ...board]) !== category || playsAlone(hole[1]!) ? 1 : 0) +
    (categoryOf([hole[0]!, ...board]) !== category || playsAlone(hole[0]!) ? 1 : 0);
  const usesBoth = holeCardsUsed === 2;

  /**
   * Playing the board: the five board cards already make the hand and the hole cards
   * add nothing. Every player still in shares it, so it is worth a chop at most, and
   * a bot that values it as a made hand will bet into a field that all holds it too.
   * The evaluator scores any number of cards, so this is one comparison.
   */
  const playingBoard = board.length === 5 && evaluate7(board as number[]) === evaluate7(all);

  /* ---------------------------------------------------------------- draws --- */
  let flushDraw = false, nutFlushDraw = false, backdoorFlush = false;
  if (flushSuit < 0 && board.length < 5) {
    for (let s = 0; s < 4; s++) {
      const held = hole.filter((c) => suitOf(c) === s).length;
      if (held === 0) continue;                       // a board flush draw is nobody's draw
      if (sc[s]! === 4) {
        flushDraw = true;
        const missing: number[] = [];
        for (let r = ALL_RANKS - 1; r >= 0; r--) {
          if (!all.some((c) => suitOf(c) === s && rankOf(c) === r)) missing.push(r);
        }
        const bestMissing = missing[0] ?? -1;
        const myBest = Math.max(...hole.filter((c) => suitOf(c) === s).map(rankOf));
        nutFlushDraw = myBest > bestMissing;
      } else if (sc[s]! === 3 && board.length === 3) {
        backdoorFlush = true;
      }
    }
  }

  let openEnded = false, gutshot = false, backdoorStraight = false;
  if (!straight && board.length >= 3) {
    const finishers = straightOuts(mask);
    /**
     * A draw only counts if the hero's straight BEATS the one the board makes.
     *
     * Refusing every rank that also completes a board straight was too blunt: T4 on
     * 9-5-6-7 was reported as a gutshot with four outs when the four eights give the
     * hero T-9-8-7-6 against the board's 9-8-7-6-5 — eight outs, and the hand is
     * ahead, not chasing. Comparing the two straights' high cards is the actual
     * question.
     */
    const boardMaskOnly = rankMask(board);
    const usesHole = finishers.filter((r) => {
      const mine = straightHigh(boardMaskOnly | (1 << r) | (1 << h0) | (1 << h1));
      const theirs = straightHigh(boardMaskOnly | (1 << r));
      return mine > theirs;
    });
    if (usesHole.length >= 2) openEnded = true;
    else if (usesHole.length === 1) gutshot = true;
    else if (board.length === 3) {
      // Two running cards. Cheap, real, and only on the flop.
      for (let r1 = 0; r1 < ALL_RANKS && !backdoorStraight; r1++) {
        if (mask & (1 << r1)) continue;
        for (let r2 = r1 + 1; r2 < ALL_RANKS; r2++) {
          if (mask & (1 << r2)) continue;
          if (hasStraight(mask | (1 << r1) | (1 << r2))
              && !hasStraight(rankMask(board) | (1 << r1) | (1 << r2))) { backdoorStraight = true; break; }
        }
      }
    }
  }

  const overcards = category === "air" && board.length > 0
    ? hole.filter((c) => rankOf(c) > topBoard).length : 0;

  /**
   * Outs, counted the way a player counts them at the table: enough to size a bet or
   * price a call, not a substitute for the equity calculation. Overlapping draws are
   * discounted rather than added, because a flush draw and an open-ender share cards.
   */
  let outs = 0;
  if (flushDraw) outs += 9;
  if (openEnded) outs += flushDraw ? 6 : 8;
  else if (gutshot) outs += flushDraw ? 3 : 4;
  if (category === "air" && !flushDraw && !openEnded && !gutshot) outs += overcards * 3;
  /**
   * Two to trips and three to a better two pair — which presupposes an unpaired
   * kicker to improve. A pocket pair below the board has neither: 77 on K-9-4 has two
   * outs, not five, and quoting five is how a bot talks itself into a call.
   */
  if (category === "pair" && (pair === "second" || pair === "third" || pair === "weak")) {
    outs += pocket ? 2 : 5;
  }

  return {
    category, pair, kicker: kickerRank(hole, board, category, pairs, trips, fullScore),
    holeCardsUsed: holeCardsUsed as 0 | 1 | 2, usesBoth, boardCategory: categoryOf(board),
    playingBoard,
    flushDraw, nutFlushDraw, backdoorFlush, openEnded, gutshot, backdoorStraight,
    overcards, outs,
  };
}

/**
 * The hole card that plays as a kicker, or -1 when neither does.
 *
 * "Plays" is the operative word and it used to be a lie: the board was ignored
 * entirely, so 4-3 on K-K-Q-J-T reported a kicker of four while the hand is K-K-Q-J-T
 * and the four is not in it. On a river that was wrong a third of the time.
 *
 * From the turn onwards the test is the evaluator's: does removing the card lower the
 * hand. On the flop every card is in the hand by construction, so a spare hole card is
 * the kicker.
 */
function kickerRank(
  hole: readonly Card[], board: readonly Card[],
  category: MadeCategory, pairs: number[], trips: number, fullScore: number
): number {
  if (category !== "pair" && category !== "two_pair" && category !== "trips") return -1;
  const made = new Set<number>([...pairs, trips].filter((r) => r >= 0));
  const spare = hole.filter((c) => !made.has(rankOf(c)));
  if (spare.length === 0) return -1;
  if (board.length < 4) return Math.max(...spare.map(rankOf));

  let best = -1;
  for (const c of spare) {
    const other = hole.find((x) => x !== c);
    if (other === undefined) continue;
    if (evaluate7([other, ...board]) < fullScore) best = Math.max(best, rankOf(c));
  }
  return best;
}


/** Highest rank appearing exactly this many times, or -1. */
function highestWithCount(rc: Int8Array, n: number): number {
  for (let r = ALL_RANKS - 1; r >= 0; r--) if (rc[r]! === n) return r;
  return -1;
}

/**
 * The made-hand category of any number of cards, from zero upward.
 *
 * Shared by the hero's hand, the board on its own, and the removal test above, so all
 * three answers are produced by the same rules. Two implementations of "is this a
 * straight" would eventually disagree, and the disagreement would show up as a bot
 * that plays one board strangely and nobody could say why.
 */
export function categoryOf(cards: readonly Card[]): MadeCategory {
  if (cards.length === 0) return "air";
  const rc = new Int8Array(ALL_RANKS);
  const sc = new Int8Array(4);
  for (const c of cards) { rc[rankOf(c)] = rc[rankOf(c)]! + 1; sc[suitOf(c)] = sc[suitOf(c)]! + 1; }

  let quads = -1, trips = -1, pairCount = 0, tripCount = 0;
  for (let r = ALL_RANKS - 1; r >= 0; r--) {
    if (rc[r]! >= 4) { if (quads < 0) quads = r; }
    else if (rc[r]! === 3) { tripCount++; if (trips < 0) trips = r; }
    else if (rc[r]! === 2) pairCount++;
  }
  // A second set plays as a full house: the lower trips fill the pair slot.
  const pairs = pairCount + Math.max(0, tripCount - 1);

  let flushSuit = -1;
  for (let s = 0; s < 4; s++) if (sc[s]! >= 5) flushSuit = s;

  const mask = rankMask(cards);
  if (flushSuit >= 0) {
    let m = 0;
    for (const c of cards) if (suitOf(c) === flushSuit) m |= 1 << rankOf(c);
    if (hasStraight(m)) return "straight_flush";
  }
  if (quads >= 0) return "quads";
  if (trips >= 0 && pairs > 0) return "full_house";
  if (flushSuit >= 0) return "flush";
  if (hasStraight(mask)) return "straight";
  if (trips >= 0) return "trips";
  if (pairs >= 2) return "two_pair";
  if (pairs === 1) return "pair";
  return "air";
}
